<script setup lang="ts">
import type { PermissionRoleInDb } from '@/api';
import { ACCESS_LEVEL_OPTIONS, ACCESS_PERMISSION_READ } from '@/utils/accessPermission';
import { computed, ref, watch, nextTick } from 'vue';
import { TemplateShareService } from '@/services/templateShareService';
import { PermissionsService } from '@/services/permissionsService';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface Props {
  show: boolean;
  templateUid: string;
  templateName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  templateName: '',
});

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'shared'): void;
}>();

const message = useGaiaToast();

const roleSearchQuery = ref('');
const roleSearchResults = ref<PermissionRoleInDb[]>([]);
const roleSearchLoading = ref(false);
const roleSearchPage = ref(1);
const roleSearchPageSize = ref(10);
const roleSearchTotal = ref(0);
const addingRoleUid = ref<string | null>(null);
/** Bitmask for new role share. Default Read. */
const selectedRolePermissionBitmask = ref<number>(ACCESS_PERMISSION_READ);

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const isTemplateReady = computed(() => Boolean(props.templateUid));

async function runInitialLoad(): Promise<void> {
  if (!props.show || !props.templateUid) return;
  roleSearchQuery.value = '';
  roleSearchPage.value = 1;
  selectedRolePermissionBitmask.value = ACCESS_PERMISSION_READ;
  await nextTick();
  await fetchRoleSearchResults();
}

watch(
  () => props.show,
  visible => {
    if (visible && props.templateUid) void runInitialLoad();
  },
  { immediate: true }
);
watch(
  () => props.templateUid,
  uid => {
    if (uid && props.show) void runInitialLoad();
  },
  { immediate: true }
);

async function fetchRoleSearchResults(): Promise<void> {
  if (!props.templateUid) return;
  roleSearchLoading.value = true;
  try {
    const result = await PermissionsService.searchRoles({
      text: roleSearchQuery.value.trim(),
      page: roleSearchPage.value,
      pageSize: roleSearchPageSize.value,
      sortKey: 'name',
      sortDirection: 'asc',
    });
    roleSearchResults.value = result.results;
    roleSearchTotal.value = result.result_count;
  } catch {
    roleSearchResults.value = [];
    roleSearchTotal.value = 0;
  } finally {
    roleSearchLoading.value = false;
  }
}

// The field owns the debounce and fires this at once when it is cleared.
function applyRoleSearch(): void {
  if (!props.show) return;
  const wasPageOne = roleSearchPage.value === 1;
  roleSearchPage.value = 1;
  // Page 1 fetches directly; any other page lets the page watcher do it.
  if (wasPageOne) void fetchRoleSearchResults();
}

watch(roleSearchPage, () => {
  if (props.show) void fetchRoleSearchResults();
});

watch(roleSearchPageSize, () => {
  if (!props.show) return;
  const wasOne = roleSearchPage.value === 1;
  roleSearchPage.value = 1;
  if (wasOne) void fetchRoleSearchResults();
});

async function handleAddRole(role: PermissionRoleInDb): Promise<void> {
  if (!props.templateUid || addingRoleUid.value) return;
  const perms = selectedRolePermissionBitmask.value;
  if (perms === 0) {
    message.warning('Choose an access level');
    return;
  }
  addingRoleUid.value = role.uid;
  try {
    await TemplateShareService.upsertRoleShare(props.templateUid, role.uid, perms);
    message.success(`Shared with role "${role.name}"`);
    emit('shared');
    emit('update:show', false);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to share with role');
  } finally {
    addingRoleUid.value = null;
  }
}

/**
 * Inline custom properties rather than `v-bind()` — the modal root is a
 * Teleport, so Vue has no root element in place to write `v-bind()` vars onto.
 */
const styleVars = {
  '--share-card-border': gaiaNeutral.borderCard,
  '--share-tile-surface': gaiaNeutral.surfaceMuted,
  '--share-tile-color': gaiaNeutral.textTertiary,
};
</script>

<template>
  <gaia-modal v-model:show="showModal" width="600px" :mask-closable="false" :style="styleVars">
    <template #header>
      <n-flex align="flex-start" :size="12" :wrap="false">
        <div class="header-tile">
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          >
            <circle cx="12" cy="3.5" r="1.8" />
            <circle cx="4" cy="8" r="1.8" />
            <circle cx="12" cy="12.5" r="1.8" />
            <path d="M5.7 7.1 10.3 4.4M5.7 8.9l4.6 2.7" />
          </svg>
        </div>
        <n-flex vertical :size="3">
          <gaia-text size="subtitle" weight="semibold" tone="ink">Share template</gaia-text>
          <gaia-text size="dense" tone="tertiary">
            Grant a role access to
            {{ props.templateName ? `“${props.templateName}”` : 'this template' }} and set
            permissions.
          </gaia-text>
        </n-flex>
      </n-flex>
    </template>

    <n-flex v-if="isTemplateReady" vertical :size="14" class="body">
      <gaia-search-input
        v-model:value="roleSearchQuery"
        variant="fill"
        placeholder="Search roles"
        :debounce="350"
        @search="applyRoleSearch"
      />

      <n-flex align="center" :size="14">
        <gaia-text size="meta" weight="semibold" tone="text">New share access:</gaia-text>
        <gaia-select
          v-model:value="selectedRolePermissionBitmask"
          :options="ACCESS_LEVEL_OPTIONS"
          style="width: 160px"
        />
      </n-flex>

      <n-flex v-if="roleSearchLoading" align="center" justify="center" class="state">
        <gaia-spinner size="small" />
      </n-flex>

      <n-flex v-else-if="roleSearchResults.length > 0" vertical :size="8" class="candidates">
        <div v-for="role in roleSearchResults" :key="role.uid" class="candidate">
          <div class="candidate-tile">
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
            >
              <circle cx="8" cy="5.4" r="2.6" />
              <path d="M2.8 13.4c.9-2.4 2.9-3.6 5.2-3.6s4.3 1.2 5.2 3.6" />
            </svg>
          </div>
          <n-flex vertical :size="1" :style="{ minWidth: 0, flex: 1 }">
            <gaia-text size="row" weight="semibold" tone="ink">{{ role.name }}</gaia-text>
            <gaia-text v-if="role.description" truncate size="meta" tone="subtle">
              {{ role.description }}
            </gaia-text>
          </n-flex>
          <gaia-button
            type="primary"
            size="small"
            :loading="addingRoleUid === role.uid"
            :disabled="addingRoleUid != null"
            @click="handleAddRole(role)"
          >
            Share
          </gaia-button>
        </div>
      </n-flex>

      <gaia-empty-state
        v-else
        :title="
          roleSearchQuery.trim() ? `No roles match “${roleSearchQuery.trim()}”` : 'No roles found'
        "
      >
        Roles are managed in the Admin Area.
      </gaia-empty-state>

      <gaia-pagination
        v-if="roleSearchTotal > 0 && !roleSearchLoading"
        :page="roleSearchPage"
        :page-size="roleSearchPageSize"
        :total="roleSearchTotal"
        :page-sizes="[5, 10, 25, 50]"
        @update:page="(page: number) => (roleSearchPage = page)"
        @update:page-size="(size: number) => (roleSearchPageSize = size)"
      />
    </n-flex>

    <gaia-empty-state v-else title="Template not selected" />

    <template #footer>
      <n-flex justify="end">
        <gaia-button size="small" @click="showModal = false">Done</gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
/* Colours arrive through the styleVars map on the card, not v-bind() — the
   modal's teleported subtree never receives component-root CSS vars. */
.header-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--share-tile-surface);
  color: var(--share-tile-color);
}

.body {
  min-height: 320px;
}

.state {
  padding: 40px 0;
}

.candidates {
  max-height: 280px;
  overflow-y: auto;
}

.candidate {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border: 1px solid var(--share-card-border);
  border-radius: 10px;
}

.candidate-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--share-tile-surface);
  color: var(--share-tile-color);
}
</style>
